import React from 'react';
import { Wrench, Zap, Car, Shield, Truck, PaintBucket } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Services = () => {
  const services = [
    {
      icon: Wrench,
      title: 'General Repairs',
      description: 'Comprehensive vehicle diagnostics, maintenance, and repair services for all makes and models.',
      features: ['Brake Service', 'Oil Changes', 'Transmission Repair', 'Engine Diagnostics']
    },
    {
      icon: Zap,
      title: 'Electrical Work',
      description: 'Expert electrical system diagnostics and repairs to keep your vehicle\'s electronics running smoothly.',
      features: ['Battery Service', 'Alternator Repair', 'Starter Replacement', 'Wiring Diagnostics']
    },
    {
      icon: PaintBucket,
      title: 'Body Work',
      description: 'Professional collision repair and bodywork to restore your vehicle to pristine condition.',
      features: ['Dent Removal', 'Paint Matching', 'Frame Repair', 'Collision Repair']
    },
    {
      icon: Shield,
      title: 'Insurance Claims',
      description: 'We work directly with insurance companies to streamline your repair process and claims.',
      features: ['Direct Billing', 'Claim Processing', 'Damage Assessment', 'Repair Estimates']
    },
    {
      icon: Truck,
      title: 'Fleet Maintenance',
      description: 'Specialized maintenance for box trucks and cube vans to keep your business moving.',
      features: ['Preventive Maintenance', 'Fleet Scheduling', 'Commercial Repairs', 'Bulk Service Rates']
    },
    {
      icon: Car,
      title: 'Full Service Care',
      description: 'Complete vehicle care services from routine maintenance to major repairs.',
      features: ['Safety Inspections', 'Tire Service', 'Exhaust Systems', 'Suspension Work']
    }
  ];

  return (
    <section id="services" className="relative py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-[#ff6b35] font-semibold text-sm uppercase tracking-wider mb-3 block">
              Our Services
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Complete Auto Care Solutions
            </h2>
            <div className="h-1 w-24 bg-[#ff6b35] mx-auto rounded-full"></div>
          </div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            From routine maintenance to complex repairs, we provide comprehensive automotive services 
            for personal vehicles and commercial fleets.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card
                key={index}
                className="bg-gradient-to-br from-[#1a1a1a] to-[#151515] border border-white/5 hover:border-[#ff6b35]/50 transition-all duration-300 group hover:transform hover:scale-105"
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-[#ff6b35]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#ff6b35]/20 transition-colors duration-300">
                    <IconComponent className="w-8 h-8 text-[#ff6b35]" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-500 text-sm">
                        <div className="w-1.5 h-1.5 bg-[#ff6b35] rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 text-lg mb-4">
            Don't see what you're looking for?
          </p>
          <a
            href="tel:416-747-6800"
            className="text-[#ff6b35] hover:text-[#e55a28] font-semibold text-lg transition-colors duration-200"
          >
            Call us at 416-747-6800 to discuss your needs
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
